import type { ReactNode } from 'react';

interface Tab {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
}

type TabVariant = 'pills' | 'underline';

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (id: string) => void;
  variant?: TabVariant;
  className?: string;
}

export function Tabs({ tabs, activeTab, onChange, variant = 'pills', className = '' }: TabsProps) {
  if (variant === 'underline') {
    return (
      <div className={`border-b border-surface-200 ${className}`}>
        <nav className="flex gap-6 -mb-px" role="tablist">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(tab.id)}
                className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-surface-500 hover:text-surface-700 hover:border-surface-300'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count != null && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                      isActive ? 'bg-primary-100 text-primary-700' : 'bg-surface-100 text-surface-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // pills variant
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-surface-600 hover:bg-surface-100 hover:text-surface-800'
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.count != null && (
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  isActive ? 'bg-primary-100 text-primary-700' : 'bg-surface-100 text-surface-600'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
